import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid, Divider,
  IconButton, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { useOrdenPedidoStore } from '../store/ordenPedidoStore';

export const OrdenPedidoFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'nueva';
  const { selectedOrden, loading, fetchOrdenById, saveOrden } = useOrdenPedidoStore();

  const [formData, setFormData] = useState<any>({ idPersona: 0, numero: '', observaciones: '', detalles: [] });

  useEffect(() => { if (isEdit && id) fetchOrdenById(parseInt(id)); }, [id]);
  useEffect(() => {
    if (isEdit && selectedOrden) {
      setFormData({
        ...selectedOrden,
        detalles: selectedOrden.detalles || [],
      });
    }
  }, [selectedOrden]);

  const handleChange = (field: string, value: any) => setFormData((prev: any) => ({ ...prev, [field]: value }));

  const handleDetalleChange = (index: number, field: string, value: any) => {
    setFormData((prev: any) => {
      const detalles = [...prev.detalles];
      detalles[index] = { ...detalles[index], [field]: value };
      return { ...prev, detalles };
    });
  };

  const addDetalle = () => setFormData((prev: any) => ({ ...prev, detalles: [...prev.detalles, { idArticulo: 0, descripcion: '', cantidad: 1, precioUnitario: 0 }] }));
  const removeDetalle = (index: number) => setFormData((prev: any) => ({ ...prev, detalles: prev.detalles.filter((_: any, i: number) => i !== index) }));

  const totales = useMemo(() => {
    let subTotal = 0;
    formData.detalles.forEach((d: any) => { subTotal += (d.cantidad || 0) * (d.precioUnitario || 0) - (d.descuento || 0); });
    const igv = subTotal * 0.18;
    return { subTotal, igv, total: subTotal + igv };
  }, [formData.detalles]);

  const handleSubmit = async () => {
    try { await saveOrden(formData); navigate('/ventas/ordenes-pedido'); } catch { /* handled */ }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/ventas/ordenes-pedido')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" fontWeight="bold">{isEdit ? 'Editar Orden de Pedido' : 'Nueva Orden de Pedido'}</Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Datos Generales</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><TextField label="ID Cliente" size="small" fullWidth type="number" value={formData.idPersona || ''} onChange={(e) => handleChange('idPersona', parseInt(e.target.value) || 0)} /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><TextField label="Número" size="small" fullWidth value={formData.numero || ''} onChange={(e) => handleChange('numero', e.target.value)} /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><TextField label="Fecha Entrega" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }} value={formData.fechaEntrega || ''} onChange={(e) => handleChange('fechaEntrega', e.target.value)} /></Grid>
          <Grid size={{ xs: 12 }}><TextField label="Observaciones" size="small" fullWidth multiline rows={2} value={formData.observaciones || ''} onChange={(e) => handleChange('observaciones', e.target.value)} /></Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Detalle</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addDetalle} size="small">Agregar Línea</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell width={50}>#</TableCell><TableCell>Descripción</TableCell><TableCell width={100}>Cantidad</TableCell><TableCell width={120}>P. Unitario</TableCell><TableCell width={120}>SubTotal</TableCell><TableCell width={50}></TableCell></TableRow></TableHead>
            <TableBody>
              {formData.detalles.map((det: any, idx: number) => (
                <TableRow key={idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell><TextField size="small" fullWidth value={det.descripcion || ''} onChange={(e) => handleDetalleChange(idx, 'descripcion', e.target.value)} /></TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.cantidad || 0} onChange={(e) => handleDetalleChange(idx, 'cantidad', parseFloat(e.target.value) || 0)} /></TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.precioUnitario || 0} onChange={(e) => handleDetalleChange(idx, 'precioUnitario', parseFloat(e.target.value) || 0)} /></TableCell>
                  <TableCell align="right">S/ {((det.cantidad || 0) * (det.precioUnitario || 0)).toFixed(2)}</TableCell>
                  <TableCell><IconButton size="small" color="error" onClick={() => removeDetalle(idx)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
              {formData.detalles.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin líneas de detalle</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ minWidth: 250 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="body2">Sub Total:</Typography><Typography variant="body2">S/ {totales.subTotal.toFixed(2)}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="body2">IGV (18%):</Typography><Typography variant="body2">S/ {totales.igv.toFixed(2)}</Typography></Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="h6">Total:</Typography><Typography variant="h6">S/ {totales.total.toFixed(2)}</Typography></Box>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={() => navigate('/ventas/ordenes-pedido')}>Cancelar</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={loading}>{loading ? 'Guardando...' : 'Guardar'}</Button>
      </Box>
    </Box>
  );
};
